import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { LiveAgents } from '@/components/LiveAgents';
import { useAuth } from '@/lib/auth';
import { RealtimeClient, type RealtimeEvent } from '@/lib/realtime';
import { formatDuration, formatPhone, formatService, formatRelative } from '@/lib/format';
import {
  Badge,
  Button,
  Card,
  CallStateBadge,
  EmptyState,
  ErrorState,
  LiveDot,
  Loading,
  PageHeader,
  ScoreBadge,
  Toast,
} from '@/components/ui';

interface LiveCall {
  id: string;
  state: string;
  bot_name: string | null;
  bot_code: string | null;
  caller_number: string | null;
  vicidial_user: string | null;
  vicidial_campaign: string | null;
  vicidial_lead_id: string | null;
  started_at: string;
  lead_score: number | null;
  services: string[] | null;
  summary: string | null;
}

const MAX_FEED = 60;

export default function LiveCalls() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [connected, setConnected] = useState(false);
  const [feed, setFeed] = useState<Array<RealtimeEvent & { key: number }>>([]);
  const feedKey = useRef(0);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['calls-live'],
    queryFn: () => api.get<{ data: LiveCall[]; live_on_this_node: number }>('/calls/live'),
    // Polling backs up the WebSocket: if the socket drops, the board still
    // refreshes rather than silently freezing on stale rows.
    // Three seconds, not fifteen. A soundboard call can be over in fifteen,
    // and if the websocket is not connected -- a proxy, a sleeping laptop --
    // polling is all the board has. A call a client could see on the dialer
    // and not here was reported against exactly that gap.
    refetchInterval: 3_000,
  });

  useEffect(() => {
    if (!user) return;
    const client = new RealtimeClient();
    client.connect();

    const unsubscribe = client.subscribe((event) => {
      if (event.type === 'connected') {
        setConnected(true);
        return;
      }

      setFeed((current) => {
        feedKey.current += 1;
        return [{ ...event, key: feedKey.current }, ...current].slice(0, MAX_FEED);
      });

      // Any call lifecycle change invalidates the board.
      if (
        event.type === 'call.started' ||
        event.type === 'call.ended' ||
        event.type === 'call.state' ||
        event.type === 'qualification'
      ) {
        queryClient.invalidateQueries({ queryKey: ['calls-live'] });
      }
    });

    return () => {
      unsubscribe();
      client.close();
      setConnected(false);
    };
  }, [user, queryClient]);

  const calls = data?.data ?? [];

  return (
    <div className="w-full">
      <PageHeader
        title="Live calls"
        description="Conversations in progress right now."
        actions={
          connected ? (
            <LiveDot label="Realtime connected" />
          ) : (
            <span className="text-xs text-ink-400">Connecting…</span>
          )
        }
      />

      {/* Who is on the dialer, as three numbers.

          This was the full agent table, above the calls, on the grounds that a
          bot can only hand a call to somebody who is logged in — which is true,
          and still the first thing to check when transfers fail. But on a
          ten-agent account it filled the entire first screen of a page called
          Live calls, and was reported exactly that way.

          Three numbers answer it almost every time. The per-agent list is one
          click below them. */}
      <div className="mb-4">
        <LiveAgents />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {isPending ? (
            <Loading />
          ) : error ? (
            <ErrorState error={error} retry={() => refetch()} />
          ) : calls.length === 0 ? (
            <Card>
              <EmptyState
                title="No calls in progress"
                description="Live conversations appear here the moment the dialer connects a customer."
              />
            </Card>
          ) : (
            <div className="space-y-3">
              {calls.map((call) => (
                <LiveCallCard key={call.id} call={call} />
              ))}
            </div>
          )}
        </div>

        <Card title="Event stream" description="Realtime, newest first" padded={false}>
          {feed.length === 0 ? (
            <p className="px-4 py-8 text-center text-xs text-ink-500">
              Waiting for events…
            </p>
          ) : (
            <ul className="max-h-[70vh] divide-y divide-line overflow-y-auto text-xs">
              {feed.map((event) => (
                <li key={event.key} className="px-4 py-2">
                  <FeedRow event={event} />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

/**
 * How long a screener call can reasonably run before it is worth a look.
 * The bot asks two questions and hands over; the silence watchdog ends a quiet
 * call in eighteen seconds. Several minutes in AI ACTIVE means something is
 * stuck, or the session is gone and only the row is left.
 */
const SUSPICIOUS_AFTER_SEC = 5 * 60;

function LiveCallCard({ call }: { call: LiveCall }) {
  const elapsed = useElapsed(call.started_at);
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );
  const { can } = useAuth();

  // A call handed to a human is a human's call now, and it can legitimately
  // run for as long as they need. Warning about it would bury the genuinely
  // stuck ones among conversations that are going fine.
  const withHuman = call.state === 'HUMAN_CONNECTED';
  const stuck = !withHuman && elapsed >= SUSPICIOUS_AFTER_SEC;

  const hangup = useMutation({
    mutationFn: () =>
      api.post<{ already_ended?: boolean }>(`/calls/${call.id}/hangup`, {}),
    onSuccess: (result: { already_ended?: boolean }) => {
      setConfirming(false);
      queryClient.invalidateQueries({ queryKey: ['live-calls'] });
      queryClient.invalidateQueries({ queryKey: ['calls'] });
      setToast({
        message: result?.already_ended
          ? 'That call had already ended.'
          : 'Call ended.',
        tone: 'good',
      });
    },
    onError: (err) => {
      setConfirming(false);
      setToast({ message: errorMessage(err, 'Could not end the call'), tone: 'bad' });
    },
  });

  return (
    <div
      className={`rounded-lg bg-surface shadow-sm ring-1 transition ${
        stuck
          ? 'ring-amber-300 dark:ring-amber-700/60'
          : 'ring-ink-200/70 hover:ring-brand-300 dark:ring-ink-800'
      }`}
    >
    <Link to={`/calls/${call.id}`} className="block p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <CallStateBadge state={call.state} />
            <span className="font-mono text-sm text-ink-900 dark:text-ink-50">
              {formatPhone(call.caller_number)}
            </span>
          </div>
          <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
            {call.bot_name ?? 'Unassigned bot'}
            {call.vicidial_user && ` · ${call.vicidial_user}`}
            {call.vicidial_campaign && ` · ${call.vicidial_campaign}`}
            {call.vicidial_lead_id && ` · lead ${call.vicidial_lead_id}`}
          </p>
        </div>
        <div className="text-right">
          <p className="font-mono text-sm tabular-nums text-ink-700 dark:text-ink-200">
            {formatDuration(elapsed)}
          </p>
          <div className="mt-1">
            <ScoreBadge score={call.lead_score} />
          </div>
        </div>
      </div>

      {call.services && call.services.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {call.services.map((service) => (
            <Badge key={service} tone="blue">
              {formatService(service)}
            </Badge>
          ))}
        </div>
      )}

      {withHuman && (
        <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-400">
          Handed to a human — the bot is no longer on this call.
        </p>
      )}

      {call.summary && (
        <p className="mt-2 line-clamp-2 text-xs text-ink-600 dark:text-ink-300">
          {call.summary}
        </p>
      )}
    </Link>

    {(stuck || confirming) && (
      <div className="border-t border-line px-4 py-2 text-xs">
        {stuck && !confirming && (
          <p className="text-amber-700 dark:text-amber-400">
            Running {formatDuration(elapsed)} — a screener call should be under a
            minute, and a transfer moves the call on within seconds. This is
            probably stuck, or its session is already gone. Calls with no session
            are closed automatically after ten minutes.
          </p>
        )}
        {confirming && (
          <p className="text-ink-700 dark:text-ink-200">
            End the call with {formatPhone(call.caller_number)}, running{' '}
            {formatDuration(elapsed)}? If someone is on the line they will be hung
            up on.
          </p>
        )}
      </div>
    )}

    {can('calls:update') && (
      <div className="flex items-center justify-end gap-2 border-t border-line px-4 py-2">
        {confirming ? (
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              onClick={() => hangup.mutate()}
              disabled={hangup.isPending}
            >
              {hangup.isPending ? 'Ending…' : 'End call'}
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={() => setConfirming(true)}>
            End call
          </Button>
        )}
      </div>
    )}

    {toast && (
      <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
    )}
    </div>
  );
}

function FeedRow({ event }: { event: RealtimeEvent }) {
  const time = event.at ? formatRelative(event.at) : '';

  switch (event.type) {
    case 'transcript':
      return (
        <div>
          <span
            className={
              event.role === 'bot'
                ? 'font-medium text-brand-600 dark:text-brand-400'
                : 'font-medium text-ink-700 dark:text-ink-200'
            }
          >
            {event.role === 'bot' ? 'Bot' : 'Customer'}
          </span>
          <span className="text-ink-600 dark:text-ink-300"> · {event.text}</span>
        </div>
      );
    case 'call.state':
      return (
        <div className="flex items-center gap-2">
          <CallStateBadge state={event.state ?? ''} />
          <span className="text-ink-400">{time}</span>
        </div>
      );
    case 'qualification':
      return (
        <div>
          <Badge tone={event.qualified ? 'green' : 'gray'}>
            {event.qualified ? 'Qualified' : 'Qualifying'}
          </Badge>{' '}
          <span className="text-ink-600 dark:text-ink-300">
            score {event.score}
            {event.services?.length ? ` · ${event.services.map(formatService).join(', ')}` : ''}
          </span>
        </div>
      );
    case 'transfer':
      return (
        <div>
          <Badge tone={event.result === 'CONNECTED' ? 'green' : 'amber'}>
            Transfer {event.result}
          </Badge>{' '}
          <span className="text-ink-600 dark:text-ink-300">
            → {event.destination}
            {event.agent ? ` (${event.agent})` : ''}
          </span>
        </div>
      );
    case 'call.started':
      return <span className="text-ink-600 dark:text-ink-300">Call started</span>;
    case 'call.ended':
      return (
        <span className="text-ink-600 dark:text-ink-300">
          Call ended{event.disposition ? ` · ${event.disposition}` : ''}
        </span>
      );
    default:
      return <span className="text-ink-500">{event.type}</span>;
  }
}

/** Seconds since `startedAt`, ticking once a second. */
function useElapsed(startedAt: string): number {
  const start = useMemo(() => new Date(startedAt).getTime(), [startedAt]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return Math.max(0, Math.floor((now - start) / 1000));
}
